import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Icon, type IconName } from "./icon";

export interface KpiProps {
  /** Leading icon shown in the `.kpi-ic` chip alongside the label. */
  icon: IconName;
  /** The headline figure (`.kpi-val`) — a formatted string or a node. */
  value: ReactNode;
  /** Caption under the value (`.kpi-sub`); also accepted as `label` for the chip row. */
  sub?: ReactNode;
  /** Text beside the icon in `.kpi-top`. */
  label: ReactNode;
  /**
   * Override the entire `.kpi-top` row (icon + label). When omitted the row is the
   * default `<span class="kpi-ic"><Icon/></span>{label}` — byte-identical to the
   * hand-rolled tile used by OpsDashboard + Dashboard.
   */
  top?: ReactNode;
  /** Optional modifier class appended to `.kpi` (e.g. a tone variant). */
  variant?: string;
  className?: string;
}

/**
 * A KPI tile: an icon-chipped label, a big value, and a faint caption. Reproduces the
 * `.kpi`/`.kpi-top`/`.kpi-ic`/`.kpi-val`/`.kpi-sub` markup from globals.css. Drop one
 * inside a `<Card>` (and a `.kpi-grid` for the dashboard layouts).
 */
export function Kpi({ icon, value, sub, label, top, variant, className }: KpiProps) {
  return (
    <div className={cn("kpi", variant, className)}>
      <div className="kpi-top">
        {top ?? (
          <>
            <span className="kpi-ic">
              <Icon name={icon} size={17} />
            </span>
            {label}
          </>
        )}
      </div>
      <div className="kpi-val">{value}</div>
      <div className="kpi-sub">{sub}</div>
    </div>
  );
}
